import apiService from "./api";
import type {
	Ticket,
	CreateTicketRequest,
	UpdateTicketRequest,
	UpdateTicketStatusRequest,
	FindTicketsRequest,
	TicketsResponse,
	DashboardSummary,
	ActiveRitual,
	MediumStats,
	MediumStatsRequest,
} from "@/types/ticket";

export const createTicket = async (data: CreateTicketRequest): Promise<Ticket> => {
	return apiService.post("/tickets", data);
};

export const getTickets = async (filters?: FindTicketsRequest): Promise<TicketsResponse> => {
	const params: any = { ...filters };
	return apiService.get("/tickets", { params });
};

export const getTicketById = async (id: string): Promise<Ticket> => {
	return apiService.get(`/tickets/${id}`);
};

export const updateTicket = async (id: string, data: UpdateTicketRequest): Promise<Ticket> => {
	return apiService.patch(`/tickets/${id}`, data);
};

export const updateTicketStatus = async (
	id: string,
	data: UpdateTicketStatusRequest,
): Promise<Ticket> => {
	return apiService.patch(`/tickets/${id}/status`, data);
};

export const deleteTicket = async (id: string): Promise<void> => {
	await apiService.delete(`/tickets/${id}`);
};

export const getActiveRituals = async (): Promise<ActiveRitual[]> => {
	return apiService.get("/tickets/active-rituals");
};

export const getDashboardSummary = async (ritualId?: string): Promise<DashboardSummary> => {
	const params = ritualId ? { ritualId } : {};
	return apiService.get("/tickets/dashboard-summary", { params });
};

export const getMediumStats = async (params: MediumStatsRequest): Promise<MediumStats> => {
	return apiService.get("/tickets/medium-stats", { params });
};

export const downloadMediumAttendancesPdf = async (
	params: MediumStatsRequest,
): Promise<void> => {
	const axios = apiService.getAxiosInstance();
	const response = await axios.get("/tickets/medium-attendances/pdf", {
		params,
		responseType: "blob",
	});
	const blob = new Blob([response.data], { type: "application/pdf" });
	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = `atendimentos-medium-${params.mediumId.slice(0, 8)}.pdf`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(objectUrl);
};

// Legacy class-based service for compatibility
export class TicketService {
	async create(data: CreateTicketRequest): Promise<Ticket> {
		return createTicket(data);
	}

	async findAll(filters?: FindTicketsRequest): Promise<TicketsResponse> {
		return getTickets(filters);
	}

	async findOne(id: string): Promise<Ticket> {
		return getTicketById(id);
	}

	async update(id: string, data: UpdateTicketRequest): Promise<Ticket> {
		return updateTicket(id, data);
	}

	async updateStatus(id: string, data: UpdateTicketStatusRequest): Promise<Ticket> {
		return updateTicketStatus(id, data);
	}

	async remove(id: string): Promise<void> {
		return deleteTicket(id);
	}

	async getActiveRituals(): Promise<ActiveRitual[]> {
		return getActiveRituals();
	}

	async getDashboardSummary(ritualId?: string): Promise<DashboardSummary> {
		return getDashboardSummary(ritualId);
	}

	async getMediumStats(params: MediumStatsRequest): Promise<MediumStats> {
		return getMediumStats(params);
	}

	async downloadMediumAttendancesPdf(params: MediumStatsRequest): Promise<void> {
		return downloadMediumAttendancesPdf(params);
	}
}

export const ticketService = new TicketService();
